import React, { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { Trash2, Shield, User, BookOpen, UploadCloud, Loader2, CheckCircle2 } from "lucide-react";
import { useSortable, SortHeader } from "../lib/sort";
import { ExportButtons } from "../lib/exports";

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bpStatus, setBpStatus] = useState(null);
  const [bpUploading, setBpUploading] = useState(false);
  const [bpError, setBpError] = useState("");
  const bpInputRef = useRef(null);
  const sort = useSortable(users, "name", "asc");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data);
    } finally { setLoading(false); }
  };

  const loadBpStatus = async () => {
    try {
      const { data } = await api.get("/admin/bot-plus/status");
      setBpStatus(data);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => { load(); loadBpStatus(); }, []);

  const uploadBp = async (file) => {
    if (!file) return;
    setBpUploading(true);
    setBpError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/admin/bot-plus/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      await loadBpStatus();
    } catch (e) {
      setBpError(e.response?.data?.detail || e.message);
    } finally { setBpUploading(false); }
  };

  const changeRole = async (id, role) => {
    await api.patch(`/admin/users/${id}`, { role });
    load();
  };

  const del = async (id) => {
    if (!window.confirm("¿Eliminar este usuario y todos sus estudios?")) return;
    await api.delete(`/admin/users/${id}`);
    load();
  };

  return (
    <div className="p-8 fade-in" data-testid="admin-users-page">
      <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Administración</div>
      <h1 className="font-display text-4xl font-extrabold tracking-tight mt-1 mb-2">Usuarios</h1>
      <p className="text-slate-600 mb-6">Gestiona los usuarios de la plataforma. Los administradores pueden ver todos los estudios.</p>

      {/* BOT PLUS catalog uploader */}
      <div className="card p-6 mb-8" data-testid="bot-plus-panel">
        <div className="flex items-center gap-3 mb-3">
          <div className="h-10 w-10 rounded-lg bg-blue-100 text-blue-700 flex items-center justify-center">
            <BookOpen size={18} />
          </div>
          <div>
            <h2 className="font-display text-lg font-bold">Catálogo BOT PLUS (Parafarmacia)</h2>
            <p className="text-xs text-slate-500">Referencia oficial para reclasificar las familias de parafarmacia al subir estudios.</p>
          </div>
        </div>
        {bpStatus && bpStatus.count > 0 ? (
          <div className="flex items-center gap-3 text-sm bg-emerald-50 border border-emerald-200 rounded px-3 py-2">
            <CheckCircle2 size={16} className="text-emerald-600" />
            <span>
              <b>{bpStatus.count.toLocaleString("es-ES")}</b> productos cargados
              {bpStatus.unique_categories && <> · {bpStatus.unique_categories} categorías</>}
              {bpStatus.uploaded_at && <> · subido {new Date(bpStatus.uploaded_at).toLocaleDateString("es-ES")}</>}
            </span>
          </div>
        ) : (
          <div className="text-sm bg-amber-50 border border-amber-200 rounded px-3 py-2 text-amber-900">
            Aún no se ha cargado el catálogo. Súbelo para que la reclasificación funcione.
          </div>
        )}
        <input
          ref={bpInputRef}
          type="file"
          accept=".xlsx,.xls,.xlsm"
          className="hidden"
          onChange={(e) => uploadBp(e.target.files?.[0])}
          data-testid="bot-plus-file-input"
        />
        <div className="mt-3 flex gap-2 items-center">
          <button
            onClick={() => bpInputRef.current?.click()}
            disabled={bpUploading}
            className="btn btn-outline btn-sm"
            data-testid="bot-plus-upload-button"
          >
            {bpUploading
              ? <><Loader2 size={14} className="animate-spin" /> Subiendo y procesando…</>
              : <><UploadCloud size={14} /> {bpStatus?.count > 0 ? "Sustituir catálogo" : "Subir catálogo BOT PLUS"}</>}
          </button>
          <p className="text-xs text-slate-500">Excel con columnas: <span className="font-mono">Código nacional · Categoría Consejo nivel 1</span></p>
        </div>
        {bpError && <div className="mt-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{bpError}</div>}
      </div>

      <div className="card overflow-x-auto">
        <div className="flex justify-end p-3 border-b border-slate-200">
          <ExportButtons
            filename="usuarios"
            title="Usuarios de la plataforma"
            sheetName="Usuarios"
            columns={[
              { header: "Nombre", key: "name" },
              { header: "Email", key: "email" },
              { header: "Farmacia", key: "pharmacy_name" },
              { header: "Rol", key: "role" },
              { header: "Creado", key: "created_at", format: (v) => v ? new Date(v).toLocaleDateString("es-ES") : "" },
            ]}
            rows={sort.sortedRows}
          />
        </div>
        <table className="dense" data-testid="admin-users-table">
          <thead>
            <tr>
              <th><SortHeader label="Nombre" k="name" sort={sort} defaultDir="asc" /></th>
              <th><SortHeader label="Email" k="email" sort={sort} defaultDir="asc" /></th>
              <th><SortHeader label="Farmacia" k="pharmacy_name" sort={sort} defaultDir="asc" /></th>
              <th><SortHeader label="Rol" k="role" sort={sort} defaultDir="asc" /></th>
              <th><SortHeader label="Creado" k="created_at" sort={sort} /></th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan="6" className="text-center py-6 text-slate-500">Cargando…</td></tr> :
              sort.sortedRows.map((u) => (
                <tr key={u.id}>
                  <td className="font-semibold">{u.name}</td>
                  <td className="font-mono text-xs">{u.email}</td>
                  <td>{u.pharmacy_name || "—"}</td>
                  <td>
                    <select value={u.role} onChange={(e) => changeRole(u.id, e.target.value)} className="input" style={{ width: 130 }} data-testid={`role-select-${u.id}`}>
                      <option value="farmacia">farmacia</option>
                      <option value="admin">admin</option>
                    </select>
                  </td>
                  <td className="font-mono text-xs">{u.created_at ? new Date(u.created_at).toLocaleDateString("es-ES") : "—"}</td>
                  <td className="text-right">
                    <button onClick={() => del(u.id)} className="text-red-600 hover:text-red-800 p-1" data-testid={`delete-user-${u.id}`}>
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))
            }
          </tbody>
        </table>
      </div>
    </div>
  );
}
